import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { getErrorMessage } from '../utils/errorMessages';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4094/api';

// Toast function will be set by initializeApi
let toastError: ((message: string) => void) | null = null;

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let axios set the Content-Type for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Get user-friendly error message
    const errorMessage = getErrorMessage(error);
    
    // Show toast notification if available
    if (toastError && !error.config?.skipToast) {
      // Handle rate limit errors with specific message
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const customMessage = retryAfter 
          ? `Too many requests. Please try again in ${retryAfter} seconds.`
          : errorMessage;
        toastError(customMessage);
      } else {
        toastError(errorMessage);
      }
    }

    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

/**
 * Initialize API with toast function
 * @param {Function} errorToast - Toast error function
 */
export function initializeApi(errorToast: (message: string) => void) {
  toastError = errorToast;
}

// Auth endpoints
export const authApi = {
  signup: (data: { name: string; email: string; password: string }) =>
    api.post('/auth/signup', data),

  login: (data: { email: string; password: string }) =>
    api.post('/auth/admin/login', data),

  getProfile: () => api.get('/auth/profile'),

  updateProfile: (data: { name?: string; email?: string }) =>
    api.put('/auth/profile', data),
};

// Story endpoints
export const storyApi = {
  getStories: (params?: { status?: string; category_id?: number; limit?: number; offset?: number }) =>
    api.post('/stories/admin/search', { status: params?.status ? [params.status] : undefined }),

  getStory: (id: string) => api.get(`/stories/admin/story/${id}`),

  createStory: (data: FormData | {
    title: string;
    one_line_brief?: string;
    category_id?: string;
    subcat_id?: string;
    tags?: string[];
  }) => api.post('/stories/admin/story', data),

  updateStory: (id: string, data: FormData | {
    title?: string;
    one_line_brief?: string;
    category_id?: string;
    subcat_id?: string;
    tags?: string[];
    status?: string;
  }) => api.put(`/stories/admin/story/${id}`, data),

  updateStoryStatus: (id: string, status: string) => api.put(`/stories/admin/story/${id}/status`, { status }),

  deleteStory: (id: string) => api.delete(`/stories/admin/story/${id}`),

  publishStory: (id: string) => api.post(`/stories/admin/story/${id}/publish`),

  validateStory: (id: string) => api.get(`/stories/admin/story/${id}/validate`),

  exportUserData: () => api.get('/stories/admin/export'),

  verifyImportPassword: (password: string) =>
    api.post('/stories/admin/verify-import-password', { password }, { skipToast: true } as object),

  getPublishedTitles: (importPassword: string) =>
    api.get('/stories/admin/published-titles', { params: { importPassword }, skipToast: true } as object),

  parseImport: (file: File, importPassword: string) => {
    const formData = new FormData();
    formData.append('importPassword', importPassword);
    formData.append('file', file);
    return api.post('/stories/admin/import/parser', formData, { skipToast: true } as object);
  },

  saveImport: (file: File, importPassword: string) => {
    const formData = new FormData();
    formData.append('importPassword', importPassword);
    formData.append('file', file);
    return api.post('/stories/admin/import/save', formData, { skipToast: true } as object);
  },

  getSlides: (storyId: string) => api.get(`/stories/admin/story/${storyId}/slides`),

  createSlide: (storyId: string, formData: FormData) =>
    api.post(`/stories/admin/story/${storyId}/slides`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  addStoryBreakToSlides: (id: string, data: FormData | {
    image?: string;
    deleteBreakCard?: string;
    _id:string;
    }) => api.put(`/stories/admin/story/${id}/break-card`, data),

    
};

// Slide endpoints
export const slideApi = {
  updateSlide: (storyId: string, slideId: string, data: FormData | { narration?: string; position?: number }) => {
    const isFormData = data instanceof FormData;
    return api.put(`/stories/admin/story/${storyId}/slides/${slideId}`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined
    });
  },

  updateSlidePosition: (storyId: string, slideId: string, position: number) =>
    api.put(`/stories/admin/story/${storyId}/slides/${slideId}/position`, { position }),

  duplicateSlide: (storyId: string, slideId: string, index: number) =>
    api.post(`/stories/admin/story/${storyId}/slides/${slideId}/duplicate`, { index }),

  deleteSlide: (storyId: string, slideId: string) => api.delete(`/stories/admin/story/${storyId}/slides/${slideId}`),
};

// Settings endpoints
export const settingsApi = {
  // Categories
  getCategories: () => api.get('/meta/admin/categories'),
  createCategory: (data: { name: string }) => api.post('/meta/admin/categories', data),
  updateCategory: (id: number, data: { name: string }) => api.put(`/meta/admin/categories/${id}`, data),
  deleteCategory: (id: number) => api.delete(`/meta/admin/categories/${id}`),

  // Subcategories
  getSubcategories: (category_id?: string) =>
    api.get(`/meta/admin/categories/${category_id}/subcategories`),
  createSubcategory: (data: { category_id: string; name: string }) =>
    api.post('/meta/admin/subcategories', { name: data.name, categoryId: data.category_id }),
  updateSubcategory: (id: number, data: { name?: string; category_id?: number }) =>
    api.put(`/meta/admin/subcategories/${id}`, data),
  deleteSubcategory: (id: number) => api.delete(`/meta/admin/subcategories/${id}`),

  // Tags
  getTags: () =>
    api.get('/meta/admin/tags'),
  getPopularTags: () =>
    api.get('/meta/admin/tags'),
  createTag: (data: { name: string }) => api.post('/meta/admin/tags', data),
  updateTag: (id: number, data: { name: string }) => api.put(`/meta/admin/tags/${id}`, data),
  deleteTag: (id: number) => api.delete(`/meta/admin/tags/${id}`),
};

// Public endpoints (no auth required)
export const publicApi = {
  getStory: (publicToken: string) => {
    const baseURL = import.meta.env.DEV ? '' : API_BASE_URL.replace('/api', '');
    return axios.get(`${baseURL}/stories/public/${publicToken}`);
  },
};

export default api;